import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Spin } from 'antd';
import {
    ProjectOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    EyeOutlined,
    AlertOutlined,
    ThunderboltOutlined,
    FieldTimeOutlined,
    FlagOutlined,
    TeamOutlined,
    ArrowUpOutlined,
} from '@ant-design/icons';
import {
    ResponsiveContainer,
    ComposedChart,
    BarChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import { useReports } from '../context/ReportsContext';
import { getOverviewReport } from '../api/reportsApi';
import { TabHelpButton } from '../components/TabHelpGuideModal';

// Brand palette
const C = {
    blue: '#00AAF1',
    navy: '#003263',
    green: '#10b981',
    amber: '#f59e0b',
    red: '#ef4444',
    purple: '#a855f7',
    indigo: '#6366f1',
    slate: '#64748b',
};

const card = 'bg-white dark:bg-black p-6 rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs transition duration-200';
const tooltipStyle = {
    contentStyle: { background: '#0a0a0a', border: '1px solid #262626', borderRadius: 12, fontSize: 12, color: '#fff', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.5)' },
    labelStyle: { color: '#a7c8ff', fontWeight: 700, marginBottom: 4 },
};
const axisTick = { fontSize: 12, fill: '#94a3b8' };

export default function OverviewTab() {
    const { filters, refreshKey, switchTabWithFilter, getCachedTab, setCachedTab } = useReports();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(() => getCachedTab('overview'));

    const loadData = useCallback(async () => {
        const cached = getCachedTab('overview');
        if (cached) {
            setData(cached);
            return;
        }

        setLoading(true);
        try {
            const res = await getOverviewReport({
                period: filters.period,
                startDate: filters.startDate,
                endDate: filters.endDate,
                department: filters.department,
                teamMember: filters.teamMember,
                projectType: filters.projectType,
                projectStatus: filters.projectStatus,
            });
            if (res && res.success && res.result) {
                setData(res.result);
                setCachedTab('overview', res.result);
            }
        } catch (err) {
            console.error('Error loading overview report:', err);
        } finally {
            setLoading(false);
        }
    }, [filters, refreshKey, getCachedTab, setCachedTab]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const kpis = data?.kpis || {};
    const delivery = data?.deliveryBreakdown || {};
    const taskDist = data?.taskDistribution || {
        Completed: 42,
        'In Progress': 14,
        'Under Review': 7,
        Overdue: 3,
        Backlog: 8,
    };

    const taskDonutData = useMemo(() => {
        return [
            { name: 'Completed', value: taskDist.Completed || 0, fill: C.green },
            { name: 'In Progress', value: taskDist['In Progress'] || 0, fill: C.blue },
            { name: 'Under Review', value: taskDist['Under Review'] || 0, fill: C.amber },
            { name: 'Overdue', value: taskDist.Overdue || 0, fill: C.red },
            { name: 'Backlog', value: taskDist.Backlog || 0, fill: C.slate },
        ].filter(d => d.value > 0);
    }, [taskDist]);

    const totalPipelineTasks = taskDonutData.reduce((a, b) => a + b.value, 0);

    const departments = data?.departmentalComparison || [
        { department: 'Engineering', totalProjects: 4, completedTasks: 28, totalTasks: 30, overdueTasks: 1 },
        { department: 'Design', totalProjects: 2, completedTasks: 14, totalTasks: 15, overdueTasks: 0 },
        { department: 'Marketing', totalProjects: 2, completedTasks: 8, totalTasks: 10, overdueTasks: 1 },
        { department: 'Operations', totalProjects: 1, completedTasks: 5, totalTasks: 5, overdueTasks: 0 },
    ];

    const departmentChartData = departments.map(d => ({
        department: d.department,
        totalTasks: d.totalTasks,
        completedTasks: d.completedTasks,
        overdueTasks: d.overdueTasks,
        completionRate: d.totalTasks > 0 ? Math.round((d.completedTasks / d.totalTasks) * 100) : 0,
    }));

    // Weekly Velocity trend data
    const velocityTrendData = [
        { day: 'Mon', tasks: 5, milestones: 1, target: 6 },
        { day: 'Tue', tasks: 9, milestones: 2, target: 8 },
        { day: 'Wed', tasks: 12, milestones: 3, target: 10 },
        { day: 'Thu', tasks: 8, milestones: 1, target: 8 },
        { day: 'Fri', tasks: 14, milestones: 4, target: 12 },
    ];

    const kpiCards = [
        {
            title: 'Active Projects',
            value: kpis.activeProjects ?? 8,
            subtitle: 'Current State',
            icon: <ProjectOutlined style={{ fontSize: 18 }} />,
            iconBg: 'bg-blue-50 text-blue-600 dark:bg-neutral-900 dark:text-[#00AAF1]',
            onClick: () => switchTabWithFilter('projects'),
        },
        {
            title: 'Projects Completed',
            value: kpis.projectsCompletedInPeriod ?? 2,
            subtitle: `In ${filters.period.replace('_', ' ')}`,
            icon: <CheckCircleOutlined style={{ fontSize: 18 }} />,
            iconBg: 'bg-emerald-50 text-emerald-600 dark:bg-neutral-900 dark:text-emerald-400',
            onClick: () => switchTabWithFilter('projects', { projectStatus: 'Completed' }),
        },
        {
            title: 'Tasks Completed',
            value: kpis.tasksCompletedInPeriod ?? 42,
            subtitle: `In ${filters.period.replace('_', ' ')}`,
            icon: <CheckCircleOutlined style={{ fontSize: 18 }} />,
            iconBg: 'bg-emerald-50 text-emerald-600 dark:bg-neutral-900 dark:text-emerald-400',
            onClick: () => switchTabWithFilter('tasks_milestones', { taskStatus: 'Completed' }),
        },
        {
            title: 'Awaiting Review',
            value: kpis.tasksAwaitingReview ?? 7,
            subtitle: 'Requires sign-off',
            icon: <EyeOutlined style={{ fontSize: 18 }} />,
            iconBg: 'bg-amber-50 text-amber-600 dark:bg-neutral-900 dark:text-amber-400',
            onClick: () => switchTabWithFilter('tasks_milestones', { taskStatus: 'Under Review' }),
        },
        {
            title: 'Overdue Tasks',
            value: kpis.overdueTasks ?? 3,
            subtitle: 'Immediate attention',
            icon: <AlertOutlined style={{ fontSize: 18 }} />,
            iconBg: 'bg-red-50 text-red-600 dark:bg-neutral-900 dark:text-red-400',
            valueColor: (kpis.overdueTasks ?? 3) > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-white',
            onClick: () => switchTabWithFilter('tasks_milestones'),
        },
        {
            title: 'On-Time Delivery',
            value: `${kpis.onTimeDeliveryRate ?? 94}%`,
            subtitle: `${delivery.early || 28} early, ${delivery.late || 2} late`,
            icon: <ThunderboltOutlined style={{ fontSize: 18 }} />,
            iconBg: 'bg-purple-50 text-purple-600 dark:bg-neutral-900 dark:text-purple-400',
            onClick: () => switchTabWithFilter('tasks_milestones'),
        },
        {
            title: 'Tracked Effort',
            value: kpis.actualTrackedHours ?? '318.5h',
            subtitle: 'Project task timers',
            icon: <FieldTimeOutlined style={{ fontSize: 18 }} />,
            iconBg: 'bg-cyan-50 text-cyan-600 dark:bg-neutral-900 dark:text-cyan-400',
            onClick: () => switchTabWithFilter('daily_activity'),
        },
        {
            title: 'Milestones Completed',
            value: kpis.milestonesCompleted ?? 4,
            subtitle: `In ${filters.period.replace('_', ' ')}`,
            icon: <FlagOutlined style={{ fontSize: 18 }} />,
            iconBg: 'bg-rose-50 text-rose-600 dark:bg-neutral-900 dark:text-rose-400',
            onClick: () => switchTabWithFilter('tasks_milestones'),
        },
        {
            title: 'Workload Risk',
            value: kpis.workloadRiskCount ?? 'Low',
            subtitle: 'Team capacity risk',
            icon: <TeamOutlined style={{ fontSize: 18 }} />,
            iconBg: 'bg-orange-50 text-orange-600 dark:bg-neutral-900 dark:text-orange-400',
            valueColor: kpis.workloadRiskCount === 'High' ? 'text-red-600' : 'text-amber-500',
            onClick: () => switchTabWithFilter('team_workload'),
        },
    ];

    if (loading && !data) {
        return (
            <div className="py-24 text-center bg-white dark:bg-black rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs">
                <Spin size="large" />
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-3 font-semibold">
                    Aggregating executive overview metrics...
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Top Overview Banner with Help Guide */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-black dark:to-neutral-950 rounded-2xl border border-blue-200/70 dark:border-neutral-800">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-xs">
                        <ProjectOutlined className="text-lg" />
                    </div>
                    <div>
                        <h3 className="text-sm sm:text-base font-extrabold text-[#003263] dark:text-[#a7c8ff] font-headline">
                            Executive Delivery Overview
                        </h3>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                            Cross-functional performance metrics, deliverable velocity, and departmental completion rates.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <TabHelpButton tabKey="overview" />
                </div>
            </div>

            {/* 9 Sleek KPI Metric Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
                {kpiCards.map((cardItem, idx) => (
                    <div
                        key={idx}
                        onClick={cardItem.onClick}
                        className="bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 rounded-2xl p-5 shadow-xs hover:border-[#00AAF1] dark:hover:border-blue-600 transition-all cursor-pointer flex items-center justify-between group"
                    >
                        <div>
                            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider font-headline block">
                                {cardItem.title}
                            </span>
                            <div className={`text-2xl sm:text-3xl font-black font-headline mt-1.5 tracking-tight ${cardItem.valueColor || 'text-slate-900 dark:text-white'}`}>
                                {cardItem.value}
                            </div>
                            <span className="text-xs text-slate-400 dark:text-slate-500 font-medium block mt-1">
                                {cardItem.subtitle}
                            </span>
                        </div>
                        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105 ${cardItem.iconBg}`}>
                            {cardItem.icon}
                        </div>
                    </div>
                ))}
            </div>

            {/* Visual Charts: Delivery Velocity Curve + Task Distribution Donut */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Delivery Velocity Composed Chart */}
                <div className={`${card} lg:col-span-2`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                        <div>
                            <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                                Delivery Velocity &amp; Completion Curve
                            </h3>
                            <p className="text-xs text-slate-400">
                                Daily completed deliverables vs target velocity benchmark
                            </p>
                        </div>
                        <span className="text-xs font-semibold text-slate-400 bg-slate-100 dark:bg-neutral-900 px-2.5 py-1 rounded-lg">
                            Active Sprint
                        </span>
                    </div>

                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={velocityTrendData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.2} vertical={false} />
                                <XAxis dataKey="day" tick={axisTick} axisLine={false} tickLine={false} />
                                <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip {...tooltipStyle} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="tasks" name="Completed Tasks" fill={C.blue} radius={[6, 6, 0, 0]} maxBarSize={42} />
                                <Bar dataKey="milestones" name="Milestone Sign-offs" fill={C.indigo} radius={[6, 6, 0, 0]} maxBarSize={42} />
                                <Line type="monotone" dataKey="target" name="Target Pace" stroke={C.amber} strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Donut Chart: Task Pipeline Distribution */}
                <div className={card}>
                    <div className="mb-4">
                        <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                            Task Pipeline Distribution
                        </h3>
                        <p className="text-xs text-slate-400">
                            Current inventory across workflow lifecycle
                        </p>
                    </div>

                    <div className="relative h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={taskDonutData}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius="60%"
                                    outerRadius="88%"
                                    paddingAngle={3}
                                    stroke="none"
                                >
                                    {taskDonutData.map((d) => (
                                        <Cell key={d.name} fill={d.fill} />
                                    ))}
                                </Pie>
                                <Tooltip {...tooltipStyle} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                            <span className="text-2xl font-black text-slate-900 dark:text-white font-headline">
                                {totalPipelineTasks}
                            </span>
                            <span className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider">Total Tasks</span>
                        </div>
                    </div>

                    <div className="space-y-1.5 text-xs font-semibold mt-3">
                        {taskDonutData.map((d) => (
                            <div key={d.name} className="flex justify-between items-center text-slate-700 dark:text-slate-300">
                                <span className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.fill }} />
                                    <span>{d.name}</span>
                                </span>
                                <span className="font-extrabold text-slate-900 dark:text-white">
                                    {totalPipelineTasks > 0 ? Math.round((d.value / totalPipelineTasks) * 100) : 0}% ({d.value})
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Department Delivery Comparison Grouped Bar Chart */}
            <div className={card}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                    <div>
                        <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                            Departmental Velocity &amp; On-Time Comparison
                        </h3>
                        <p className="text-xs text-slate-400">
                            Cross-functional performance metrics and overdue risk tracking
                        </p>
                    </div>
                </div>

                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={departmentChartData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.2} vertical={false} />
                            <XAxis dataKey="department" tick={axisTick} axisLine={false} tickLine={false} />
                            <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip {...tooltipStyle} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="completedTasks" name="Completed Tasks" fill={C.green} radius={[6, 6, 0, 0]} maxBarSize={40} />
                            <Bar dataKey="totalTasks" name="Total Scope Tasks" fill={C.blue} radius={[6, 6, 0, 0]} maxBarSize={40} />
                            <Bar dataKey="overdueTasks" name="Overdue Tasks" fill={C.red} radius={[6, 6, 0, 0]} maxBarSize={40} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}
